import React, { useState, useEffect, useCallback, useContext } from "react";
import { Button, Input, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Swal from "sweetalert2";
import Header from "../../layout/Header/Header";
import { AuthContext } from "../../contexts/AuthContext";
import { posGet, posPost, posPatch, avisaError, mensajeError, dinero, hoy, sumaDias, fechaHora } from "./posApi";
import { useTienda, TiendaSelector } from "./TiendaSelector";
import SinPermiso from "./SinPermiso";
import { ticketCaja } from "./posTickets";

const TIPOS = {
  Gasto: { icono: "fas fa-receipt", color: "#ef4444", titulo: "Registrar gasto", ayuda: "Pago en efectivo desde la caja" },
  Retiro: { icono: "fas fa-hand-holding-usd", color: "#f59e0b", titulo: "Retiro de efectivo", ayuda: "Depósito al banco o entrega a gerente" },
  Fondo: { icono: "fas fa-coins", color: "#10b981", titulo: "Fondo de caja", ayuda: "Efectivo que se mete para cambio" },
};
const VACIO = { concepto: "", descripcion: "", recibio: "", importe: "" };

function PosCaja() {
  const { user } = useContext(AuthContext);
  const ctx = useTienda(false);
  const { tienda } = ctx;
  const [estado, setEstado] = useState(null);
  const [form, setForm] = useState(null); // { tipo, ...VACIO }
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [inicio, setInicio] = useState(sumaDias(hoy(), -7));
  const [fin, setFin] = useState(hoy());

  const cargar = useCallback(() => {
    if (!tienda) return;
    posGet("/caja/estado", { area: tienda }).then(setEstado).catch(avisaError);
    posGet("/caja", { area: tienda, inicio, fin }).then(setHistorial).catch(() => setHistorial([]));
  }, [tienda, inicio, fin]);
  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const m = await posPost("/caja", { area: tienda, tipo: form.tipo, ...form, importe: Number(form.importe) });
      ticketCaja(m);
      Swal.fire({ icon: "success", title: `${m.tipo} ${m.folio} registrado`, text: "Se imprimió el comprobante para firma.", timer: 2000, showConfirmButton: false });
      setForm(null);
      cargar();
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se registró", text: mensajeError(e) });
    } finally { setGuardando(false); }
  };

  const cancelar = async (m) => {
    const r = await Swal.fire({ title: `¿Cancelar ${m.tipo.toLowerCase()} ${m.folio}?`, text: `${m.concepto} · ${dinero(m.importe)}`, icon: "warning", showCancelButton: true, confirmButtonText: "Sí, cancelar", cancelButtonText: "No" });
    if (!r.value) return;
    try { await posPatch(`/caja/${m._id}/cancelar`, { area: tienda }); cargar(); } catch (e) { avisaError(e); }
  };

  if (!(user && user.rolTiendas && user.posCaja)) return <SinPermiso que="registrar gastos y retiros de caja" />;

  const t = form && TIPOS[form.tipo];
  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-wallet mr-2" style={{ color: "#f59e0b" }} />Gastos y retiros de caja</h3>
            <p className="page-subtitle">Todo entra al siguiente corte de caja de la tienda</p>
          </div>
          <div className="page-actions">
            <TiendaSelector ctx={ctx} />
            <Button color="secondary" size="sm" href="/PuntoVenta"><i className="fas fa-th-large mr-1" />Menú</Button>
          </div>
        </div>

        {estado && (
          <div className="row">
            <div className="col-lg-4 mb-3">
              <div className="form-section" style={{ height: "100%" }}>
                <div className="form-section-header dark"><i className="fas fa-cash-register" /><h4 className="form-section-title">Efectivo en caja ahora</h4></div>
                <div className="form-section-body">
                  <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "#0f172a" }}>{dinero(estado.efectivoEnCaja)}</div>
                  <table className="table table-sm mb-0" style={{ fontSize: "0.85rem" }}>
                    <tbody>
                      <tr><td>Ventas en efectivo</td><td className="text-right">{dinero(estado.ventasEfectivo)}</td></tr>
                      {estado.reembolsadoEfectivo > 0 && <tr><td>Devoluciones en efectivo</td><td className="text-right text-danger">−{dinero(estado.reembolsadoEfectivo)}</td></tr>}
                      {estado.fondos > 0 && <tr><td>Fondo de caja</td><td className="text-right text-success">+{dinero(estado.fondos)}</td></tr>}
                      {estado.gastos > 0 && <tr><td>Gastos</td><td className="text-right text-danger">−{dinero(estado.gastos)}</td></tr>}
                      {estado.retiros > 0 && <tr><td>Retiros</td><td className="text-right text-danger">−{dinero(estado.retiros)}</td></tr>}
                    </tbody>
                  </table>
                  <div className="small text-muted mt-1">Desde el último corte de la tienda.</div>
                </div>
              </div>
            </div>
            <div className="col-lg-8 mb-3">
              <div className="row">
                {Object.entries(TIPOS).map(([k, v]) => (
                  <div key={k} className="col-md-4 mb-3">
                    <button type="button" onClick={() => setForm({ tipo: k, ...VACIO })}
                      style={{ width: "100%", height: "100%", border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff", padding: "22px 12px", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
                      <div style={{ width: 46, height: 46, borderRadius: 12, background: `${v.color}14`, color: v.color, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem" }}><i className={v.icono} /></div>
                      <div style={{ fontWeight: 700, marginTop: 8 }}>{v.titulo}</div>
                      <div className="small text-muted">{v.ayuda}</div>
                    </button>
                  </div>
                ))}
              </div>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead><tr><th>Folio</th><th>Hora</th><th>Tipo</th><th>Concepto</th><th className="text-right">Importe</th><th>Registró</th><th /></tr></thead>
                  <tbody>
                    {!estado.movimientos.length && <tr><td colSpan={7} className="text-center text-muted">Sin movimientos desde el último corte</td></tr>}
                    {estado.movimientos.map((m) => (
                      <tr key={m._id} style={m.estatus === "Cancelado" ? { color: "#94a3b8", textDecoration: "line-through" } : {}}>
                        <td>{m.folio}</td><td>{fechaHora(m.fecha)}</td>
                        <td><i className={`${TIPOS[m.tipo].icono} mr-1`} style={{ color: TIPOS[m.tipo].color }} />{m.tipo}</td>
                        <td>{m.concepto}{m.descripcion ? <div className="small text-muted">{m.descripcion}</div> : null}{m.recibio ? <div className="small text-muted">Recibió: {m.recibio}</div> : null}</td>
                        <td className="text-right"><strong>{m.tipo === "Fondo" ? "+" : "−"}{dinero(m.importe)}</strong></td>
                        <td>{m.user ? m.user.nombre : ""}</td>
                        <td>{m.estatus === "Activo" && <Button size="sm" color="danger" onClick={() => cancelar(m)} title="Cancelar"><i className="fas fa-ban" /></Button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="form-section">
          <div className="form-section-header dark"><i className="fas fa-history" /><h4 className="form-section-title">Historial</h4></div>
          <div className="form-section-body">
            <div className="filter-bar">
              <div><label>Desde</label><Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></div>
              <div><label>Hasta</label><Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} /></div>
              {["Gasto", "Retiro", "Fondo"].map((k) => (
                <div key={k} className="stat-chip" style={{ alignSelf: "center" }}>{k}s <span>{dinero(historial.filter((m) => m.tipo === k && m.estatus === "Activo").reduce((s, m) => s + m.importe, 0))}</span></div>
              ))}
            </div>
            <div className="table-responsive">
              <table className="table table-sm">
                <thead><tr><th>Folio</th><th>Fecha</th><th>Tipo</th><th>Concepto</th><th className="text-right">Importe</th><th>Registró</th><th>Estatus</th><th /></tr></thead>
                <tbody>
                  {!historial.length && <tr><td colSpan={8} className="text-center text-muted">Sin movimientos en el periodo</td></tr>}
                  {historial.map((m) => (
                    <tr key={m._id} style={m.estatus === "Cancelado" ? { color: "#94a3b8" } : {}}>
                      <td>{m.folio}</td><td>{fechaHora(m.fecha)}</td><td>{m.tipo}</td>
                      <td>{m.concepto}{m.descripcion ? ` · ${m.descripcion}` : ""}</td>
                      <td className="text-right">{dinero(m.importe)}</td><td>{m.user ? m.user.nombre : ""}</td>
                      <td>{m.estatus === "Cancelado" ? <span className="badge badge-secondary">Cancelado</span> : m.cortes ? <span className="badge badge-info">En corte</span> : <span className="badge badge-success">Activo</span>}</td>
                      <td><Button size="sm" color="info" onClick={() => ticketCaja(m)} title="Comprobante"><i className="fas fa-print" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={!!form} toggle={() => setForm(null)}>
        <ModalHeader toggle={() => setForm(null)}>{t && <><i className={`${t.icono} mr-2`} style={{ color: t.color }} />{t.titulo}</>}</ModalHeader>
        {form && (
          <ModalBody>
            {form.tipo !== "Fondo" && estado && <div className="alert alert-light py-2" style={{ border: "1px solid #e2e8f0" }}>Hay <strong>{dinero(estado.efectivoEnCaja)}</strong> de efectivo en caja.</div>}
            <label>{form.tipo === "Gasto" ? "¿En qué se gastó?" : "Concepto"}</label>
            <Input list="conceptos-caja" value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} placeholder={form.tipo === "Gasto" ? "Ej. Papelería" : ""} autoFocus />
            <datalist id="conceptos-caja">{((estado && estado.conceptos[form.tipo]) || []).map((c) => <option key={c} value={c} />)}</datalist>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
              {((estado && estado.conceptos[form.tipo]) || []).slice(0, 7).map((c) => (
                <Button key={c} size="sm" color="light" style={{ border: "1px solid #e2e8f0" }} onClick={() => setForm({ ...form, concepto: c })}>{c}</Button>
              ))}
            </div>
            <label className="mt-3">Importe</label>
            <Input type="number" min="0" step="0.01" inputMode="decimal" style={{ fontSize: "1.3rem", fontWeight: 700 }} value={form.importe} onChange={(e) => setForm({ ...form, importe: e.target.value })} />
            {form.tipo === "Retiro" && (<><label className="mt-3">¿Quién recibe el efectivo?</label><Input value={form.recibio} onChange={(e) => setForm({ ...form, recibio: e.target.value })} placeholder="Nombre" /></>)}
            <label className="mt-3">Nota (opcional)</label>
            <Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder={form.tipo === "Gasto" ? "Ej. rollos para la impresora de tickets" : ""} />
          </ModalBody>
        )}
        <ModalFooter>
          <Button color="secondary" onClick={() => setForm(null)}>Cancelar</Button>
          <Button color="success" disabled={guardando || !(Number(form && form.importe) > 0) || !(form && form.concepto.trim())} onClick={guardar}>
            {guardando ? <i className="fas fa-spinner fa-spin" /> : "Registrar e imprimir"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default PosCaja;
